/** How a lead shows: its name, the first-reply clock, its type and its duplicate-check flag. */
import { Badge } from "@/components/ui/badge";

import type { ZcrmAccount } from "../providers/zinspector/accounts";
import {
  MATCH_LABELS,
  MATCH_TONES,
  type LeadMatch,
} from "../providers/zinspector/leadMatching";
import {
  leadClock,
  sourceLabel,
  type ZcrmLead,
} from "../providers/zinspector/leads";

/** The lead's company, or the person, or the email for a chat that ended early. */
export const leadTitle = (
  lead: Pick<ZcrmLead, "company" | "person" | "email">,
) => lead.company || lead.person || lead.email || "Unnamed lead";

export const leadSubtitle = (lead: ZcrmLead) =>
  [
    lead.person && lead.person !== lead.company ? lead.person : "",
    lead.markets.join(", "),
  ]
    .filter(Boolean)
    .join(" · ") || "Name and market not given";

const CLOCK_VARIANT = {
  late: "error",
  soon: "warning",
  ok: "neutral",
  done: "neutral",
} as const;

export const ClockBadge = ({ lead, now }: { lead: ZcrmLead; now?: Date }) => {
  const clock = leadClock(lead, now);
  return (
    <Badge variant={CLOCK_VARIANT[clock.kind]} title="First reply">
      {clock.label}
    </Badge>
  );
};

export const TypeBadge = ({ type }: { type: string }) =>
  type ? (
    <Badge
      variant={
        type === "PM company"
          ? "info"
          : type === "Individual landlord"
            ? "warning"
            : "neutral"
      }
    >
      {type === "PM company"
        ? "PMC"
        : type === "Individual landlord"
          ? "Individual"
          : type}
    </Badge>
  ) : null;

export const formatLeadDoors = (doors: number | null) =>
  doors ? `${doors.toLocaleString("en-US")} doors` : "—";

export const leadSource = (lead: ZcrmLead) =>
  sourceLabel(lead.source) || "Source not recorded";

/** The flag a match shows; an existing client names who it is routed to. */
export const matchLabel = (
  match: LeadMatch,
  managerName?: (account: ZcrmAccount) => string,
) => {
  if (match.kind === "existing_client" && match.account && managerName) {
    const name = managerName(match.account);
    return `${MATCH_LABELS.existing_client}${name ? ` · routed to ${name}` : ""}`;
  }
  return MATCH_LABELS[match.kind];
};

export const MatchBadge = ({
  match,
  label,
}: {
  match: LeadMatch;
  label?: string;
}) => (
  <Badge variant={MATCH_TONES[match.kind]} title={match.reason}>
    {label ?? MATCH_LABELS[match.kind]}
  </Badge>
);

/** The order flags show in the inbox: what changes who handles the lead comes first. */
const INBOX_FLAG_ORDER = [
  "support",
  "existing_client",
  "same_company",
  "outside_markets",
  "unfinished",
] as const;

/** The flags worth showing in the inbox: the same person is merged silently, so it isn't one. */
export const inboxFlags = (matches: LeadMatch[]) =>
  INBOX_FLAG_ORDER.flatMap((kind) => matches.filter((m) => m.kind === kind));
